import { externalEmulatorConfig, isExternalEmulatorKey } from './externalEmulators.js';
import { retroRomUrl, romApiUrl, romListApiUrl } from './romApi.js';
import { SYSTEMS, vimmToEjsKey } from './systems.js';

export const SUPPORTED_EXTS = {
  gen: 1, md: 1, smd: 1, bin: 1, '32x': 1, iso: 1, cue: 1,
  sms: 1, gg: 1,
  smc: 1, sfc: 1, fig: 1, swc: 1,
  nes: 1, fds: 1, unf: 1, nsf: 1,
  n64: 1, z64: 1, v64: 1,
  gba: 1, gb: 1, gbc: 1,
  nds: 1,
  pbp: 1, chd: 1, img: 1, mdf: 1,
  zip: 1,
  a26: 1,
  '3ds': 1, cci: 1, cia: 1,
  wbfs: 1, wad: 1, rvz: 1, ciso: 1, gcm: 1, gc: 1,
};

export const UNSUPPORTED_EXTS = {};

export function fileExtLower(name) {
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot + 1).toLowerCase() : '';
}

export function isRomFile(name) {
  return !!name && !name.startsWith('.') && !!SUPPORTED_EXTS[fileExtLower(name)];
}

export function isUnsupportedRom(name) {
  return !!UNSUPPORTED_EXTS[fileExtLower(name)];
}

export function acceptToExts(accept) {
  return accept.split(',').map((x) => x.trim().toLowerCase());
}

export function fileMatchesExts(filename, exts) {
  const lower = filename.toLowerCase();
  return exts.some((ext) => lower.endsWith(ext));
}

export function guessSystemKey(filename, catalogEntry = null, zipIndex = null) {
  if (catalogEntry?.ejsKey) return catalogEntry.ejsKey;
  if (catalogEntry?.internalTitle) {
    const fromInternal = guessSystemKey(catalogEntry.internalTitle, null, zipIndex);
    if (fromInternal) return fromInternal;
  }
  const l = filename.toLowerCase();
  if (/\.zip$/i.test(l) && zipIndex?.[filename]?.ejsKey) {
    return zipIndex[filename].ejsKey;
  }
  if (/\.(gen|smd|32x)$/i.test(l)) return 'segaMD';
  if (/\.md$/i.test(l)) return 'segaMD';
  if (/\.(sms|gg)$/i.test(l)) return 'segaMS';
  if (/\.(smc|sfc|fig|swc)$/i.test(l)) return 'snes';
  if (/\.(nes|fds|unf|nsf)$/i.test(l)) return 'nes';
  if (/\.a26$/i.test(l)) return 'atari2600';
  if (/\.(n64|z64|v64)$/i.test(l)) return 'n64';
  if (/\.gba$/i.test(l)) return 'gba';
  if (/\.gbc$/i.test(l)) return 'gb';
  if (/\.gb$/i.test(l)) return 'gb';
  if (/\.nds$/i.test(l)) return 'nds';
  if (/\.pbp$/i.test(l) || /\.chd$/i.test(l)) return 'psx';
  if (/\.(3ds|cci|cia)$/i.test(l)) return '3ds';
  if (/\.(wbfs|wad|rvz|ciso|gcm|gc)$/i.test(l)) return 'wii';
  return null;
}

export function extTag(filename) {
  const dot = filename.lastIndexOf('.');
  return dot >= 0 ? filename.slice(dot + 1).toUpperCase() : '';
}

export function pickThumbnail(entry) {
  if (entry.thumbnail) return entry.thumbnail;
  const img = entry.images;
  if (!img) return null;
  return (
    img.box?.publicPath ||
    img.boxLarge?.publicPath ||
    img.cart?.[0]?.publicPath ||
    img.screen?.[0]?.publicPath ||
    null
  );
}

export function pickCartThumbnailFromImages(images, fallback = null) {
  if (images?.cart?.[0]?.publicPath) return images.cart[0].publicPath;
  return fallback;
}

export async function fetchPublicRomFiles(appUrlFn) {
  try {
    const r = await fetch(romListApiUrl(), { credentials: 'include', cache: 'no-store' });
    if (r.ok) {
      const data = await r.json();
      return Array.isArray(data.files) ? data.files : [];
    }
  } catch {
    /* fallback */
  }
  try {
    const r = await fetch(appUrlFn('api/roms-list'), { credentials: 'include', cache: 'no-store' });
    if (r.ok) {
      const data = await r.json();
      return Array.isArray(data.files) ? data.files : [];
    }
  } catch {
    /* fallback */
  }
  try {
    const r = await fetch(appUrlFn('roms/manifest.json'), { cache: 'no-store' });
    if (r.ok) {
      const data = await r.json();
      return Array.isArray(data.files) ? data.files : [];
    }
  } catch {
    /* ignore */
  }
  return [];
}

export async function fetchVaultManifest(appUrlFn) {
  try {
    const r = await fetch(appUrlFn('vault/manifest.json'), { cache: 'no-store' });
    if (!r.ok) return [];
    const data = await r.json();
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

export async function fetchRomCatalog(appUrlFn) {
  try {
    const r = await fetch(appUrlFn('roms/catalog.json'), { cache: 'no-store' });
    if (!r.ok) return {};
    const data = await r.json();
    return data?.entries && typeof data.entries === 'object' ? data.entries : {};
  } catch {
    return {};
  }
}

let zipIndexCache = null;

export async function fetchZipIndex(appUrlFn) {
  if (zipIndexCache) return zipIndexCache;
  try {
    const r = await fetch(appUrlFn('roms/zip-index.json'), { cache: 'no-store' });
    if (!r.ok) return {};
    const data = await r.json();
    zipIndexCache = data?.entries && typeof data.entries === 'object' ? data.entries : {};
    return zipIndexCache;
  } catch {
    return {};
  }
}

function resolveZipSystemFromVault(filename, vaultEntries) {
  if (!/\.zip$/i.test(filename)) return null;
  const base = filename.replace(/\.zip$/i, '').toLowerCase().trim();
  for (const entry of vaultEntries) {
    const internal =
      entry.details?.internalTitle || entry.media?.[0]?.GoodTitleDecoded || '';
    if (!internal) continue;
    const internalBase = internal.replace(/\.[^.]+$/, '').toLowerCase().trim();
    if (base === internalBase) {
      return vimmToEjsKey(entry.system, entry.systemCode);
    }
  }
  return null;
}

/** Agrupa entradas do manifesto Vimm + ROMs órfãs por consola (chave EJS). */
export function buildConsoleGroups(vaultEntries, publicRomFiles, searchTerm, romCatalog = {}, zipIndex = {}) {
  const q = (searchTerm || '').trim().toLowerCase();
  const matches = (title) => !q || title.toLowerCase().includes(q);

  const groups = new Map();
  const coveredFiles = new Set();

  for (const entry of vaultEntries) {
    const hasRom = !!(entry.rom?.publicPath || entry.rom?.filename);
    const filename = hasRom
      ? entry.rom.filename || entry.rom.publicPath.split('/').pop()
      : null;
    if (filename) coveredFiles.add(filename);

    const ejsKey =
      vimmToEjsKey(entry.system, entry.systemCode) ||
      (filename ? guessSystemKey(filename) : null) ||
      entry._ejsKey ||
      'unknown';
    const label = entry.system || entry.systemCode || 'Outros';

    if (!matches(entry.title || String(entry.vaultId))) continue;

    if (!groups.has(ejsKey)) {
      groups.set(ejsKey, { ejsKey, vimmSystem: label, games: [] });
    }
    groups.get(ejsKey).games.push({
      id: `vault-${entry.vaultId}`,
      vaultId: entry.vaultId,
      title: entry.title || filename || `Vault ${entry.vaultId}`,
      filename,
      url: hasRom ? retroRomUrl(filename) : null,
      apiUrl: hasRom ? romApiUrl(filename) : null,
      thumbnail: pickThumbnail(entry),
      cartThumbnail: pickCartThumbnailFromImages(entry.images, pickThumbnail(entry)),
      year: entry.details?.year,
      overall: entry.details?.ratings?.overall,
      region: entry.details?.region?.label,
      downloadSize: entry.details?.downloadSize,
      fromVault: true,
      hasRom,
      needsDownload: !hasRom,
      ejsKey,
      externalEmulator: isExternalEmulatorKey(ejsKey) ? externalEmulatorConfig(ejsKey)?.emulatorName : null,
      unsupported: false,
    });
  }

  for (const name of publicRomFiles) {
    if (!isRomFile(name) || coveredFiles.has(name)) continue;
    if (!matches(name)) continue;

    const unsupported = isUnsupportedRom(name);
    const cat = romCatalog[name];
    let ejsKey =
      guessSystemKey(name, cat, zipIndex) ||
      resolveZipSystemFromVault(name, vaultEntries) ||
      'unknown';
    const url = retroRomUrl(name);
    const title = cat?.title || name;
    const external = isExternalEmulatorKey(ejsKey);

    if (!matches(title) && !matches(name)) continue;

    if (!groups.has(ejsKey)) {
      groups.set(ejsKey, { ejsKey, vimmSystem: null, games: [] });
    }
    groups.get(ejsKey).games.push({
      id: `file-${name}`,
      title,
      filename: name,
      url,
      thumbnail: cat?.thumbnail || null,
      cartThumbnail: cat?.thumbnail || null,
      internalTitle: cat?.internalTitle || null,
      fromVault: false,
      ejsKey,
      externalEmulator: external ? externalEmulatorConfig(ejsKey)?.emulatorName : null,
      hasRom: true,
      needsDownload: false,
      unsupported: false,
      unsupportedLabel: null,
    });
  }

  for (const g of groups.values()) {
    g.games.sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }));
  }

  const order = ['nes', 'snes', 'n64', 'gba', 'gb', 'segaMD', 'segaMS', 'atari2600', 'nds', 'psx', '3ds', 'wii', 'unknown'];
  return [...groups.values()].sort((a, b) => {
    const ia = order.indexOf(a.ejsKey);
    const ib = order.indexOf(b.ejsKey);
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
  });
}

function titleFirstLetter(title) {
  const t = String(title || '').trim();
  const stripped = t.replace(/^(the|a|an|o|a|os|as|um|uma)\s+/i, '');
  const ch = (stripped[0] || t[0] || '#').toUpperCase();
  return /[A-ZÀ-ÖØ-Þ]/.test(ch) ? ch : '#';
}

/** Sub-agrupa jogos já ordenados por título (A–Z, # no fim). */
export function groupGamesByLetter(games) {
  const map = new Map();
  for (const game of games) {
    const letter = titleFirstLetter(game.title);
    if (!map.has(letter)) map.set(letter, []);
    map.get(letter).push(game);
  }
  return [...map.entries()]
    .sort(([a], [b]) => {
      if (a === '#') return 1;
      if (b === '#') return -1;
      return a.localeCompare(b, undefined, { sensitivity: 'base' });
    })
    .map(([letter, items]) => ({ letter, games: items }));
}

export function presetsForSystem(key, publicRomFiles) {
  const s = SYSTEMS[key];
  if (!s) return [];
  const exts = acceptToExts(s.accept);
  const seen = new Set();
  const items = [];

  for (const p of s.presets) {
    if (seen.has(p.url)) continue;
    seen.add(p.url);
    items.push({ url: p.url, label: p.label });
  }

  for (const name of publicRomFiles) {
    if (!fileMatchesExts(name, exts)) continue;
    const url = retroRomUrl(name);
    if (seen.has(url)) continue;
    seen.add(url);
    items.push({ url, label: name });
  }

  return items;
}
